import React, { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Grid2X2 } from "lucide-react"
import {
 buildMostVisitedShortcuts,
 buildQuickShortcuts,
 type QuickShortcutNavigationItem,
} from "helpers/quickShortcuts"
import { rankedVisitLinks, readPageVisits } from "helpers/pageVisits"
import { resolvePageVisitScope } from "helpers/pageVisits"
import { useAuth } from "slices/user/hooks"
import { resolveSidebarIcon } from "helpers/resolveSidebarIcon"
import { cn } from "../lib/utils"
import { Button } from "./ui/button"
import {
 Dialog,
 DialogBody,
 DialogContent,
 DialogDescription,
 DialogHeader,
 DialogTitle,
 DialogTrigger,
} from "./ui/dialog"

interface QuickShortcutsMenuProps {
 menuItems?: QuickShortcutNavigationItem[]
 trigger?: React.ReactElement
 onNavigate?: () => void
}

function ShortcutCard({
 item,
 onNavigate,
}: {
 item: QuickShortcutNavigationItem
 onNavigate: () => void
}) {
 const Icon = resolveSidebarIcon(item.icon)

 return (
  <Link
   to={item.link ?? "/"}
   onClick={onNavigate}
   className={cn(
    "group flex min-h-[4.75rem] min-w-0 flex-col justify-between rounded-xl border border-border-subtle bg-card px-3 py-2.5 text-start transition-colors sm:min-h-[5.25rem] sm:px-4 sm:py-3",
    "hover:border-primary/35 hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30",
   )}
  >
   <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-icon transition-colors group-hover:bg-primary group-hover:text-primary-foreground sm:h-9 sm:w-9">
    <Icon className="h-4 w-4" aria-hidden />
   </span>
   <span className="mt-2 truncate text-xs font-semibold text-foreground sm:text-sm">
    {item.label}
   </span>
  </Link>
 )
}

function ShortcutGrid({
 items,
 onNavigate,
}: {
 items: QuickShortcutNavigationItem[]
 onNavigate: () => void
}) {
 return (
  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-2.5 lg:grid-cols-4">
   {items.map((item) => (
    <ShortcutCard
     key={item.id ?? item.link}
     item={item}
     onNavigate={onNavigate}
    />
   ))}
  </div>
 )
}

export function QuickShortcutsMenu({
 menuItems = [],
 trigger,
 onNavigate,
}: QuickShortcutsMenuProps) {
 const { t, i18n } = useTranslation()
 const { user } = useAuth()
 const visitScope = resolvePageVisitScope(user?.id)
 const [open, setOpen] = useState(false)
 const shortcuts = useMemo(() => buildQuickShortcuts(menuItems), [menuItems])
 const mostVisited = useMemo(
  () => buildMostVisitedShortcuts(menuItems, rankedVisitLinks(readPageVisits(visitScope))),
  // open: refresh visit ranking when the dialog opens
  // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute on open
  [menuItems, open, visitScope?.tenant, visitScope?.userId],
 )
 const remainingShortcuts = useMemo(() => {
  const featured = new Set(
   mostVisited.map((item) => item.id ?? item.link).filter(Boolean),
  )
  return shortcuts.filter((item) => !featured.has(item.id ?? item.link))
 }, [mostVisited, shortcuts])

 const closeAndNavigate = () => {
  setOpen(false)
  onNavigate?.()
 }

 if (shortcuts.length === 0 && mostVisited.length === 0) return null

 return (
 <Dialog open={open} onOpenChange={setOpen}>
 <DialogTrigger asChild>
 {trigger ?? (
 <Button
 type="button"
 variant="ghost"
 size="icon-sm"
 className="shrink-0"
 aria-label={t("dashboard.shortcuts.title")}
 title={t("dashboard.shortcuts.title")}
 >
 <Grid2X2 className="h-4.5 w-4.5" aria-hidden />
 </Button>
 )}
 </DialogTrigger>
 <DialogContent
 dir={i18n.dir()}
 className="gap-0 overflow-hidden p-0 sm:max-w-3xl lg:max-w-5xl"
 >
 <DialogHeader className="border-b border-border-subtle px-4 py-3 text-start sm:px-5 sm:py-4">
 <DialogTitle className="text-base sm:text-lg">
 {t("dashboard.shortcuts.title")}
 </DialogTitle>
 <DialogDescription className="sr-only">
 {t("dashboard.shortcuts.description")}
 </DialogDescription>
 </DialogHeader>
 <DialogBody className="max-h-[min(70dvh,40rem)] space-y-4 p-3 sm:max-h-[min(75dvh,44rem)] sm:p-5">
 {mostVisited.length > 0 ? (
  <section className="space-y-2">
   <h3 className="text-xs font-semibold text-muted-foreground sm:text-sm">
    {t("dashboard.shortcuts.mostVisited")}
   </h3>
   <ShortcutGrid items={mostVisited} onNavigate={closeAndNavigate} />
  </section>
 ) : null}
 {remainingShortcuts.length > 0 ? (
  <section className="space-y-2">
   {mostVisited.length > 0 ? (
    <h3 className="text-xs font-semibold text-muted-foreground sm:text-sm">
     {t("dashboard.shortcuts.title")}
    </h3>
   ) : null}
   <ShortcutGrid items={remainingShortcuts} onNavigate={closeAndNavigate} />
  </section>
 ) : null}
 </DialogBody>
 </DialogContent>
 </Dialog>
 )
}
